import React from 'react';
import { Box, Button, Container, Typography, useTheme, alpha } from '@mui/material';
import { COMPANY, FACTS } from '../../data/siteContent';
import { SECTION_SCROLL_MARGIN } from '../../utils/scroll';
import { MotionBox, fadeInUp, useMotionProps, useReducedMotion } from '../motion';
import SectionHeading from './SectionHeading';

const Facts = () => {
  const theme = useTheme();
  const motionProps = useMotionProps();
  const reducedMotion = useReducedMotion();

  return (
    <Box
      id="facts"
      component="section"
      aria-labelledby="facts-heading"
      sx={{
        scrollMarginTop: SECTION_SCROLL_MARGIN,
        py: { xs: 9, md: 14 },
        position: 'relative',
        overflow: 'hidden',
        color: '#fff',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.main} 100%)`
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(38rem 24rem at 20% 80%, ${alpha('#fff', 0.12)} 0%, transparent 55%),
                       radial-gradient(38rem 24rem at 80% 20%, ${alpha('#fff', 0.12)} 0%, transparent 55%)`
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <SectionHeading
          id="facts-heading"
          eyebrow="Our Impact"
          title="Measurable Success Stories"
          subtitle="Numbers that showcase our commitment to excellence and the trust our clients place in us."
          onDark
        />

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2.5, sm: 3, md: 4 },
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            alignItems: 'stretch'
          }}
        >
          {FACTS.map((fact, index) => {
            const Icon = fact.icon;
            return (
              <MotionBox
                key={fact.suffix}
                {...motionProps(fadeInUp(index * 0.1))}
                whileHover={reducedMotion ? undefined : { y: -6 }}
                sx={{
                  textAlign: 'center',
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  ...theme.surfaces.glassDark,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'background-color 0.3s ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: fact.gradient
                  }
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: { xs: 76, md: 92 },
                    height: { xs: 76, md: 92 },
                    borderRadius: '50%',
                    background: fact.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: { xs: 2, md: 3 },
                    color: '#fff',
                    boxShadow: `0 12px 32px ${alpha('#000', 0.22)}`
                  }}
                >
                  <Icon sx={{ fontSize: { xs: 38, md: 46 } }} />
                </Box>

                {/* Solid white keeps the figure legible; the gradient stays on the icon. */}
                <Typography
                  variant="h3"
                  component="p"
                  sx={{ fontWeight: 900, mb: 1, color: '#fff', fontVariantNumeric: 'tabular-nums' }}
                >
                  {fact.count}
                </Typography>
                <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mb: 1.5, color: alpha('#fff', 0.92) }}>
                  {fact.suffix}
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#fff', 0.82), lineHeight: 1.6 }}>
                  {fact.description}
                </Typography>
              </MotionBox>
            );
          })}
        </Box>

        <MotionBox
          {...motionProps(fadeInUp(0.2))}
          sx={{
            textAlign: 'center',
            mt: { xs: 6, md: 8 },
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            ...theme.surfaces.glassDark
          }}
        >
          <Typography variant="h4" component="h3" sx={{ fontWeight: 800, mb: 2, color: '#fff' }}>
            Join Our Success Story
          </Typography>
          <Typography variant="h6" component="p" sx={{ color: alpha('#fff', 0.9), mb: 4, maxWidth: 620, mx: 'auto', fontWeight: 400 }}>
            Be part of Sri Lanka's leading digital transformation journey. Let us build something remarkable together.
          </Typography>
          <Button
            variant="contained"
            size="large"
            component="a"
            href={COMPANY.appUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 700,
              borderRadius: 3,
              background: '#fff',
              color: 'primary.dark',
              boxShadow: `0 12px 32px ${alpha('#000', 0.2)}`,
              '&:hover': { background: alpha('#fff', 0.92), transform: 'translateY(-2px)' },
              '&:focus-visible': { outline: '3px solid #fff', outlineOffset: 3 }
            }}
          >
            Start Your Journey
          </Button>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Facts;
